import { useState, useRef } from "react";
import { Upload, X, Check, AlertTriangle, FileText, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* ---------- CSV parsing ---------- */
function parseCSV(text) {
  const clean = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  const firstLine = clean.split("\n", 1)[0] || "";
  const delim = (firstLine.split(";").length > firstLine.split(",").length) ? ";" : ",";
  const rows = [];
  const lines = clean.split("\n");
  for (const raw of lines) {
    if (!raw.trim() && rows.length === 0) continue;
    if (!raw.trim()) { rows.push([]); continue; }
    const cells = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (inQuote) {
        if (c === '"') {
          if (raw[i + 1] === '"') { cur += '"'; i++; }
          else inQuote = false;
        } else cur += c;
      } else {
        if (c === '"') inQuote = true;
        else if (c === delim) { cells.push(cur); cur = ""; }
        else cur += c;
      }
    }
    cells.push(cur);
    rows.push(cells.map((s) => s.trim()));
  }
  return rows.filter((r) => r.length && r.some((c) => c !== ""));
}

const HEADER_MAP = {
  "telefon numarasi": "phone",
  "telefon": "phone",
  "phone": "phone",
  "marka adi": "brand_name",
  "marka": "brand_name",
  "brand": "brand_name",
  "yetkili adi": "contact_name",
  "yetkili": "contact_name",
  "contact": "contact_name",
  "sektor": "sector",
  "sector": "sector",
  "ihtiyac turu": "needs",
  "hizmet turu": "needs",
  "needs": "needs",
  "fiyat bilgisi": "price_info",
  "fiyat": "price_info",
  "price": "price_info",
  "not": "note",
  "note": "note",
  "renk etiketi": "status",
  "renk": "status",
  "durum": "status",
  "status": "status",
};

function normalizeHeader(h) {
  return (h || "")
    .toLowerCase()
    .replace(/ı/g, "i").replace(/İ/g, "i")
    .replace(/ş/g, "s").replace(/Ş/g, "s")
    .replace(/ğ/g, "g").replace(/Ğ/g, "g")
    .replace(/ü/g, "u").replace(/Ü/g, "u")
    .replace(/ö/g, "o").replace(/Ö/g, "o")
    .replace(/ç/g, "c").replace(/Ç/g, "c")
    .trim();
}

function mapCsvToRows(matrix) {
  if (!matrix.length) return [];
  const header = matrix[0].map(normalizeHeader);
  const fieldByCol = header.map((h) => HEADER_MAP[h] || null);
  return matrix.slice(1).map((r) => {
    const obj = { phone: "", brand_name: "", contact_name: "", sector: "", needs: "", price_info: "", note: "", status: "" };
    fieldByCol.forEach((f, i) => { if (f) obj[f] = r[i] || ""; });
    return obj;
  });
}

const SAMPLE_CSV = `Telefon Numarası,Marka Adı,Yetkili Adı,Sektör,İhtiyaç Türü,Fiyat Bilgisi,Not,Renk Etiketi
5551234567,Glow Beauty,Ayşe Yılmaz,Güzellik,"Reklam, Sosyal Medya",20000 TL,İlk görüşme yapıldı teklif iletildi,Olumlu
1234,Eski Kayıt,,Diğer,,,Sadece son 4 hane biliniyor,İşlemde
5559998877,Fit Studio,Mehmet Demir,Spor,Reklam,,Meşguldü tekrar aranacak,İşlemde
`;

/* ---------- Component ---------- */
export default function ImportModal({ open, onClose, onImported }) {
  const [step, setStep] = useState("upload"); // upload | preview | result
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null); // {summary, results}
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [importing, setImporting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const reset = () => {
    setStep("upload");
    setRows([]);
    setPreview(null);
    setIncludeDuplicates(false);
    setFinalResult(null);
    setError("");
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => { reset(); onClose(); };

  const handleFile = async (file) => {
    setError("");
    setFileName(file.name);
    try {
      const text = await file.text();
      const matrix = parseCSV(text);
      if (matrix.length < 2) {
        setError("CSV dosyası boş görünüyor veya sadece başlık satırı var.");
        return;
      }
      const parsedRows = mapCsvToRows(matrix);
      if (parsedRows.length === 0) {
        setError("Ayrıştırılabilir satır bulunamadı. Başlıkları kontrol edin.");
        return;
      }
      setRows(parsedRows);
      // Dry run for preview
      const { data } = await api.post("/calls/import", { rows: parsedRows, dry_run: true, include_duplicates: false });
      setPreview(data);
      setStep("preview");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "CSV okunamadı."));
    }
  };

  const commit = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/calls/import", { rows, dry_run: false, include_duplicates: includeDuplicates });
      setFinalResult(data);
      setStep("result");
      toast.success(`${data.summary.imported} kayıt içe aktarıldı`);
      onImported?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ornek-cagrilar.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="import-modal">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm max-w-[720px] w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <Upload size={16} className="text-slate-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 tracking-tight">CSV İçe Aktar</h3>
              <p className="text-xs text-slate-500 mt-0.5">Toplu çağrı kaydını CSV dosyasından yükleyin</p>
            </div>
          </div>
          <button onClick={close} data-testid="import-close" className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="import-error">
              {error}
            </div>
          )}

          {step === "upload" && (
            <div data-testid="import-step-upload">
              <div className="rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-300 transition-colors p-10 text-center">
                <FileText size={28} className="text-slate-400 mx-auto mb-3" />
                <div className="text-sm font-medium text-slate-900 mb-1">CSV dosyanızı seçin</div>
                <div className="text-xs text-slate-500 mb-5">
                  Beklenen kolonlar: Telefon Numarası, Marka Adı, Yetkili Adı, Sektör, İhtiyaç Türü, Fiyat Bilgisi, Not, Renk Etiketi
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="import-pick-file"
                  className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-5 py-2 font-medium text-sm transition-colors duration-150"
                >
                  Dosya Seç
                </button>
              </div>

              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-xs text-slate-600 flex items-start gap-3">
                <div className="flex-1 leading-relaxed">
                  <div className="font-medium text-slate-800 mb-1">Kurallar</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Telefon Numarası zorunlu. Format esnektir — 4 haneli kısa numaralar da kabul edilir.</li>
                    <li>Renk Etiketi: İşlemde / Olumlu / Olumsuz / Anlaşıldı. Boşsa "İşlemde" atanır.</li>
                    <li>Not alanı doluysa kaydın güncelleme geçmişine ilk giriş olarak eklenir.</li>
                    <li>Zaten kayıtlı numaralar için onayınız istenecek.</li>
                  </ul>
                </div>
                <button
                  onClick={downloadSample}
                  data-testid="import-sample"
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 font-medium"
                >
                  <Download size={12} /> Örnek CSV
                </button>
              </div>
            </div>
          )}

          {step === "preview" && preview && (
            <div data-testid="import-step-preview">
              <div className="text-xs text-slate-500 mb-1">Dosya: <span className="text-slate-700 font-medium">{fileName}</span></div>
              <div className="grid grid-cols-4 gap-3 mb-5 mt-3">
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Toplam</div>
                  <div className="text-2xl font-bold text-slate-900" data-testid="preview-total">{preview.summary.total}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">Geçerli</div>
                  <div className="text-2xl font-bold text-emerald-700" data-testid="preview-ok">
                    {preview.results.filter((r) => r.status === "ok").length}
                  </div>
                </div>
                <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-1">Duplicate</div>
                  <div className="text-2xl font-bold text-orange-600" data-testid="preview-dup">{preview.summary.duplicates}</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50/40 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-1">Hatalı</div>
                  <div className="text-2xl font-bold text-red-600" data-testid="preview-err">{preview.summary.errors}</div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 overflow-hidden mb-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/60">
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">#</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Telefon</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Marka</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.results.slice(0, 100).map((r) => {
                      const row = rows[r.index] || {};
                      const badge =
                        r.status === "ok"
                          ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium"><Check size={11} /> Geçerli</span>
                          : r.status === "duplicate"
                          ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-orange-50 text-orange-600 text-xs font-medium"><AlertTriangle size={11} /> Kayıtlı</span>
                          : <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-xs font-medium"><AlertTriangle size={11} /> Hatalı</span>;
                      return (
                        <tr key={r.index} className="border-b border-slate-100 last:border-none" data-testid={`preview-row-${r.index}`}>
                          <td className="py-2 px-4 text-slate-400 text-xs tabular-nums">{r.index + 1}</td>
                          <td className="py-2 px-4 text-slate-700 tabular-nums">{row.phone || <span className="text-slate-300">—</span>}</td>
                          <td className="py-2 px-4 text-slate-700">
                            {row.brand_name || <span className="text-slate-300">—</span>}
                            {r.status === "error" && <div className="text-xs text-red-600 mt-0.5">{r.error}</div>}
                            {r.status === "duplicate" && r.matches?.[0] && (
                              <div className="text-xs text-orange-600 mt-0.5">Eşleşen: {r.matches[0].brand_name || r.matches[0].phone}</div>
                            )}
                          </td>
                          <td className="py-2 px-4">{badge}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {preview.results.length > 100 && (
                  <div className="px-4 py-2 text-xs text-slate-400 bg-slate-50/60 border-t border-slate-100">
                    İlk 100 satır gösteriliyor. Toplam {preview.results.length} satır.
                  </div>
                )}
              </div>

              {preview.summary.duplicates > 0 && (
                <label className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50/40 p-4 mb-2 cursor-pointer" data-testid="dup-toggle-label">
                  <input
                    type="checkbox"
                    checked={includeDuplicates}
                    onChange={(e) => setIncludeDuplicates(e.target.checked)}
                    className="mt-0.5"
                    data-testid="dup-toggle"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      {preview.summary.duplicates} kayıt zaten var. Yine de eklensin mi?
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      İşaretlerseniz duplicate numaralar da yeni kayıt olarak eklenir. İşaretlemezseniz atlanır.
                    </div>
                  </div>
                </label>
              )}
            </div>
          )}

          {step === "result" && finalResult && (
            <div className="text-center py-6" data-testid="import-step-result">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <Check size={26} className="text-emerald-600" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 tracking-tight mb-2">İçe Aktarma Tamamlandı</h4>
              <p className="text-sm text-slate-500 mb-6">
                <span className="text-emerald-700 font-medium" data-testid="result-imported">{finalResult.summary.imported}</span> kayıt eklendi
                {finalResult.summary.duplicates > 0 && (
                  <> · <span className="text-orange-600 font-medium" data-testid="result-skipped">{finalResult.summary.duplicates}</span> duplicate atlandı</>
                )}
                {finalResult.summary.errors > 0 && (
                  <> · <span className="text-red-600 font-medium" data-testid="result-errors">{finalResult.summary.errors}</span> hata</>
                )}
              </p>

              {finalResult.summary.errors > 0 && (
                <div className="mt-4 text-left rounded-lg border border-red-100 bg-red-50/40 p-4 text-xs">
                  <div className="font-semibold text-red-700 mb-2">Hatalı Satırlar</div>
                  <ul className="space-y-1">
                    {finalResult.results.filter((r) => r.status === "error").slice(0, 10).map((r) => (
                      <li key={r.index} className="text-red-600">Satır {r.index + 1}: {r.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/40">
          {step === "upload" && (
            <button
              onClick={close}
              data-testid="import-cancel"
              className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-5 py-2 text-sm font-medium transition-colors duration-150"
            >
              İptal
            </button>
          )}

          {step === "preview" && (
            <>
              <button
                onClick={() => { reset(); }}
                data-testid="import-back"
                className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-5 py-2 text-sm font-medium transition-colors duration-150"
              >
                Başka Dosya
              </button>
              <button
                onClick={commit}
                disabled={importing || (preview.summary.total - preview.summary.errors - (includeDuplicates ? 0 : preview.summary.duplicates)) === 0}
                data-testid="import-commit"
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-6 py-2 text-sm font-medium transition-colors duration-150 disabled:opacity-50"
              >
                {importing ? "İçe Aktarılıyor..." : `${preview.summary.total - preview.summary.errors - (includeDuplicates ? 0 : preview.summary.duplicates)} Kayıt İçe Aktar`}
              </button>
            </>
          )}

          {step === "result" && (
            <button
              onClick={close}
              data-testid="import-done"
              className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-6 py-2 text-sm font-medium transition-colors duration-150"
            >
              Kapat
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
